import { clientHasTradesSuppliersModuleAccess } from "./clientPortalEffectiveAccessModel";
import {
  clientPortalDesignBoardsTabAllowed,
  clientPortalSpecificationsTabAllowed,
  clientPortalTradesSuppliersTabAllowed,
} from "./clientPortalProjectOperatorAccessModel";
import {
  resolveSchedulePortalCapabilities,
  scheduleTabAllowedForContributor,
} from "./projectScheduleCapabilityModel";
import { resolveClientPortalFutureModuleVisibility } from "./clientPortalModuleApplicabilityModel";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import {
  assignmentUsesStaffFinancialPortalTabModel,
  buildStaffPortalTabsWhenAccountingEnabled,
} from "./projectStaffFinancialPortalModel";

export const PROJECT_TABS = Object.freeze({
  OVERVIEW: "overview",
  PLANNING: "planning",
  PHOTOS: "photos",
  DRAWINGS_REVIEW: "drawings_review",
  RENDERINGS: "renderings",
  DOCUMENTS_REQUESTS: "documents_requests",
  TIME_LOG: "time_log",
  INVOICES: "invoices",
  // Legacy client workflow tabs retained as optional content keys
  SCHEDULE: "schedule",
  CONSTRUCTION: "construction",
  FINANCIALS: "financials",
  RESOURCES: "resources",
  MESSAGES: "messages",
  TRADES_SUPPLIERS: "trades_suppliers",
  SPECIFICATIONS: "specifications",
  DESIGN_BOARDS: "design_boards",
});

export const PROJECT_TAB_LABELS = Object.freeze({
  [PROJECT_TABS.OVERVIEW]: "Overview",
  [PROJECT_TABS.PLANNING]: "Planning",
  [PROJECT_TABS.PHOTOS]: "Photos",
  [PROJECT_TABS.DRAWINGS_REVIEW]: "Drawings & review",
  [PROJECT_TABS.RENDERINGS]: "Renderings",
  [PROJECT_TABS.DOCUMENTS_REQUESTS]: "Documents & requests",
  [PROJECT_TABS.TIME_LOG]: "Time Log",
  [PROJECT_TABS.INVOICES]: "Invoices",
  [PROJECT_TABS.SCHEDULE]: "Schedule",
  [PROJECT_TABS.CONSTRUCTION]: "Construction",
  [PROJECT_TABS.FINANCIALS]: "Financials",
  [PROJECT_TABS.RESOURCES]: "Permits & Resources",
  [PROJECT_TABS.MESSAGES]: "Messages",
  [PROJECT_TABS.TRADES_SUPPLIERS]: "Trades & suppliers",
  [PROJECT_TABS.SPECIFICATIONS]: "Specifications",
  [PROJECT_TABS.DESIGN_BOARDS]: "Design boards",
});

/** Legacy query / state values → canonical content key. */
const LEGACY_TAB_ALIASES = Object.freeze({
  overview: PROJECT_TABS.OVERVIEW,
  planning: PROJECT_TABS.PLANNING,
  photos: PROJECT_TABS.PHOTOS,
  photo: PROJECT_TABS.PHOTOS,
  drawings: PROJECT_TABS.DRAWINGS_REVIEW,
  drawing: PROJECT_TABS.DRAWINGS_REVIEW,
  drawings_review: PROJECT_TABS.DRAWINGS_REVIEW,
  renderings: PROJECT_TABS.RENDERINGS,
  rendering: PROJECT_TABS.RENDERINGS,
  files: PROJECT_TABS.DRAWINGS_REVIEW,
  review: PROJECT_TABS.DRAWINGS_REVIEW,
  documents: PROJECT_TABS.DOCUMENTS_REQUESTS,
  document: PROJECT_TABS.DOCUMENTS_REQUESTS,
  documents_requests: PROJECT_TABS.DOCUMENTS_REQUESTS,
  client_docs: PROJECT_TABS.DOCUMENTS_REQUESTS,
  docs: PROJECT_TABS.DOCUMENTS_REQUESTS,
  time: PROJECT_TABS.TIME_LOG,
  time_log: PROJECT_TABS.TIME_LOG,
  timelog: PROJECT_TABS.TIME_LOG,
  invoices: PROJECT_TABS.INVOICES,
  invoice: PROJECT_TABS.INVOICES,
  schedule: PROJECT_TABS.SCHEDULE,
  construction: PROJECT_TABS.CONSTRUCTION,
  financials: PROJECT_TABS.FINANCIALS,
  resources: PROJECT_TABS.RESOURCES,
  messages: PROJECT_TABS.MESSAGES,
  trades_suppliers: PROJECT_TABS.TRADES_SUPPLIERS,
  trades: PROJECT_TABS.TRADES_SUPPLIERS,
  suppliers: PROJECT_TABS.TRADES_SUPPLIERS,
  specifications: PROJECT_TABS.SPECIFICATIONS,
  project_specifications: PROJECT_TABS.SPECIFICATIONS,
  design_boards: PROJECT_TABS.DESIGN_BOARDS,
  approvals: PROJECT_TABS.DRAWINGS_REVIEW, // retired → drawings
});

function safeStr(value) {
  return String(value ?? "").trim();
}

export function normalizeProjectPortalTabId(raw) {
  const key = safeStr(raw).toLowerCase();
  if (!key) return "";
  if (LEGACY_TAB_ALIASES[key]) return LEGACY_TAB_ALIASES[key];
  if (Object.values(PROJECT_TABS).includes(key)) return key;
  return "";
}

/**
 * Whether a contributor (engineer/designer) may keep this tab selected.
 * Documents & Photos must NOT redirect to drawings.
 */
export function isContributorAllowedProjectTab(tabId) {
  const key = normalizeProjectPortalTabId(tabId);
  return (
    key === PROJECT_TABS.OVERVIEW ||
    key === PROJECT_TABS.PLANNING ||
    key === PROJECT_TABS.PHOTOS ||
    key === PROJECT_TABS.DRAWINGS_REVIEW ||
    key === PROJECT_TABS.RENDERINGS ||
    key === PROJECT_TABS.DOCUMENTS_REQUESTS ||
    key === PROJECT_TABS.TIME_LOG ||
    key === PROJECT_TABS.INVOICES ||
    // CL-ACCESS-007A-R3H-R1: staff Financials tab visibility still gated by canAccessFinancials.
    key === PROJECT_TABS.FINANCIALS ||
    key === PROJECT_TABS.TRADES_SUPPLIERS ||
    key === PROJECT_TABS.SPECIFICATIONS ||
    key === PROJECT_TABS.DESIGN_BOARDS
  );
}

export function buildVisibleProjectPortalTabs({
  permissions = {},
  project = null,
  isPastProject = false,
  isSharedFilesOnlyViewer = false,
  packageAvailable = false,
  isClientPersona = false,
  isContributorPersona = false,
  hasAuthorizedRenderings = false,
  assignment = null,
  accountingPermissionsUiEnabled = null,
} = {}) {
  const resolvedAssignment = assignment || {
    permissions,
    accessScope: "full_project",
    status: "active",
  };

  if (projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    // CL-ACCESS-CLIENT-MODULES-001CD-R2: explicit assignment permissions govern staff Financials tab model.
    const useStaffFinancialPortal = assignmentUsesStaffFinancialPortalTabModel({
      assignment: resolvedAssignment,
      accountingPermissionsUiEnabled,
    });
    if (useStaffFinancialPortal) {
      return buildStaffPortalTabsWhenAccountingEnabled({
        permissions,
        project,
        assignment: resolvedAssignment,
        accountingPermissionsUiEnabled,
        isPastProject,
        isSharedFilesOnlyViewer,
        packageAvailable,
        isClientPersona,
        isContributorPersona,
        hasAuthorizedRenderings,
      });
    }
  }

  if (isPastProject) {
    const tabs = [{ key: PROJECT_TABS.OVERVIEW, label: PROJECT_TAB_LABELS[PROJECT_TABS.OVERVIEW] }];
    if (hasAuthorizedRenderings) {
      tabs.push({ key: PROJECT_TABS.RENDERINGS, label: PROJECT_TAB_LABELS[PROJECT_TABS.RENDERINGS] });
    }
    if (permissions.canReviewDrawings) {
      tabs.push({
        key: PROJECT_TABS.DRAWINGS_REVIEW,
        label: PROJECT_TAB_LABELS[PROJECT_TABS.DRAWINGS_REVIEW],
      });
    }
    return tabs;
  }

  const tabs = [{ key: PROJECT_TABS.OVERVIEW, label: PROJECT_TAB_LABELS[PROJECT_TABS.OVERVIEW] }];

  if (permissions.canViewPlanning) {
    const showPlanning =
      !isSharedFilesOnlyViewer ||
      Boolean(packageAvailable);
    if (showPlanning) {
      tabs.push({ key: PROJECT_TABS.PLANNING, label: PROJECT_TAB_LABELS[PROJECT_TABS.PLANNING] });
    }
  }

  if (permissions.canViewPhotos) {
    tabs.push({ key: PROJECT_TABS.PHOTOS, label: PROJECT_TAB_LABELS[PROJECT_TABS.PHOTOS] });
  }

  if (hasAuthorizedRenderings) {
    tabs.push({ key: PROJECT_TABS.RENDERINGS, label: PROJECT_TAB_LABELS[PROJECT_TABS.RENDERINGS] });
  }

  if (permissions.canReviewDrawings) {
    tabs.push({
      key: PROJECT_TABS.DRAWINGS_REVIEW,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.DRAWINGS_REVIEW],
    });
  }

  // Documents: uploaders always; contributors/clients with upload; engineers get documents when upload OR review
  const showDocuments =
    Boolean(permissions.canUploadFiles) ||
    (isClientPersona && Boolean(permissions.canUploadFiles)) ||
    (isContributorPersona && Boolean(permissions.canUploadFiles));
  if (showDocuments) {
    tabs.push({
      key: PROJECT_TABS.DOCUMENTS_REQUESTS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.DOCUMENTS_REQUESTS],
    });
  }

  if (permissions.canLogTime || permissions.canViewTimeLog) {
    tabs.push({ key: PROJECT_TABS.TIME_LOG, label: PROJECT_TAB_LABELS[PROJECT_TABS.TIME_LOG] });
  }

  // CL-INVOICE-001A3: staff/contributor preparers with canManageInvoices see the workspace Invoices tab.
  // Client persona uses client-safe issued/test invoice grants — never the staff invoice workspace tab.
  if (permissions.canManageInvoices === true && !isClientPersona) {
    tabs.push({ key: PROJECT_TABS.INVOICES, label: PROJECT_TAB_LABELS[PROJECT_TABS.INVOICES] });
  }

  if (
    clientPortalTradesSuppliersTabAllowed(permissions)
    && !tabs.some((item) => item.key === PROJECT_TABS.TRADES_SUPPLIERS)
  ) {
    tabs.push({
      key: PROJECT_TABS.TRADES_SUPPLIERS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.TRADES_SUPPLIERS],
    });
  }

  if (
    clientPortalSpecificationsTabAllowed(permissions, resolvedAssignment)
    && !tabs.some((item) => item.key === PROJECT_TABS.SPECIFICATIONS)
  ) {
    tabs.push({
      key: PROJECT_TABS.SPECIFICATIONS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.SPECIFICATIONS],
    });
  }

  if (
    clientPortalDesignBoardsTabAllowed(permissions, resolvedAssignment)
    && !tabs.some((item) => item.key === PROJECT_TABS.DESIGN_BOARDS)
  ) {
    tabs.push({
      key: PROJECT_TABS.DESIGN_BOARDS,
      label: PROJECT_TAB_LABELS[PROJECT_TABS.DESIGN_BOARDS],
    });
  }

  // CL-CLIENT-UX-001: future modules require explicit permission + stage/applicability.
  // Client persona / accessScope / full_project must not auto-inject these tabs.
  const scheduleCaps = resolveSchedulePortalCapabilities({
    isAdmin: false,
    isClientPersona,
    permissions,
    assignment: resolvedAssignment,
  });
  if (isContributorPersona && scheduleTabAllowedForContributor(scheduleCaps)) {
    tabs.push({ key: PROJECT_TABS.SCHEDULE, label: PROJECT_TAB_LABELS[PROJECT_TABS.SCHEDULE] });
  }

  if (isClientPersona && !isContributorPersona) {
    const future = resolveClientPortalFutureModuleVisibility({ permissions, project });
    if (future.schedule) {
      tabs.push({ key: PROJECT_TABS.SCHEDULE, label: PROJECT_TAB_LABELS[PROJECT_TABS.SCHEDULE] });
    }
    if (future.construction) {
      tabs.push({ key: PROJECT_TABS.CONSTRUCTION, label: PROJECT_TAB_LABELS[PROJECT_TABS.CONSTRUCTION] });
    }
    if (future.financials) {
      tabs.push({ key: PROJECT_TABS.FINANCIALS, label: PROJECT_TAB_LABELS[PROJECT_TABS.FINANCIALS] });
    }
    if (future.resources) {
      tabs.push({ key: PROJECT_TABS.RESOURCES, label: PROJECT_TAB_LABELS[PROJECT_TABS.RESOURCES] });
    }
    if (future.messages) {
      tabs.push({ key: PROJECT_TABS.MESSAGES, label: PROJECT_TAB_LABELS[PROJECT_TABS.MESSAGES] });
    }
  }

  return orderProjectPortalTabs(tabs, { isContributorPersona, isClientPersona });
}

export function orderProjectPortalTabs(tabs = [], { isContributorPersona = false } = {}) {
  const preferred = isContributorPersona
    ? [
        PROJECT_TABS.OVERVIEW,
        PROJECT_TABS.PHOTOS,
        PROJECT_TABS.RENDERINGS,
        PROJECT_TABS.DRAWINGS_REVIEW,
        PROJECT_TABS.DOCUMENTS_REQUESTS,
        PROJECT_TABS.PLANNING,
        PROJECT_TABS.TIME_LOG,
        PROJECT_TABS.INVOICES,
        PROJECT_TABS.SCHEDULE,
      ]
    : [
        PROJECT_TABS.OVERVIEW,
        PROJECT_TABS.PLANNING,
        PROJECT_TABS.PHOTOS,
        PROJECT_TABS.RENDERINGS,
        PROJECT_TABS.DRAWINGS_REVIEW,
        PROJECT_TABS.DOCUMENTS_REQUESTS,
        PROJECT_TABS.TIME_LOG,
        PROJECT_TABS.INVOICES,
        PROJECT_TABS.SCHEDULE,
        PROJECT_TABS.CONSTRUCTION,
        PROJECT_TABS.FINANCIALS,
        PROJECT_TABS.RESOURCES,
        PROJECT_TABS.MESSAGES,
        PROJECT_TABS.TRADES_SUPPLIERS,
        PROJECT_TABS.SPECIFICATIONS,
      ];
  const byKey = new Map((Array.isArray(tabs) ? tabs : []).map((t) => [t.key, t]));
  const out = [];
  preferred.forEach((key) => {
    if (byKey.has(key)) {
      out.push(byKey.get(key));
      byKey.delete(key);
    }
  });
  byKey.forEach((t) => out.push(t));
  return out;
}

/**
 * Single resolver for requested tab → visible content + nav active key.
 */
export function resolveProjectPortalTab({
  requestedTab = "",
  visibleTabs = [],
  fallbackTab = PROJECT_TABS.OVERVIEW,
} = {}) {
  const normalized = normalizeProjectPortalTabId(requestedTab);
  const keys = new Set((Array.isArray(visibleTabs) ? visibleTabs : []).map((t) => t.key || t));
  const fallback =
    normalizeProjectPortalTabId(fallbackTab) ||
    (keys.has(PROJECT_TABS.OVERVIEW) ? PROJECT_TABS.OVERVIEW : [...keys][0] || PROJECT_TABS.OVERVIEW);

  if (normalized && keys.has(normalized)) {
    return {
      requested: normalized,
      contentKey: normalized,
      navActiveKey: normalized,
      allowed: true,
      fallbackUsed: false,
    };
  }

  return {
    requested: normalized || safeStr(requestedTab).toLowerCase(),
    contentKey: fallback,
    navActiveKey: fallback,
    allowed: false,
    fallbackUsed: true,
  };
}

export function isDrawingsReviewTab(tabId) {
  return normalizeProjectPortalTabId(tabId) === PROJECT_TABS.DRAWINGS_REVIEW;
}

export function isRenderingsTab(tabId) {
  return normalizeProjectPortalTabId(tabId) === PROJECT_TABS.RENDERINGS;
}

export function isDocumentsRequestsTab(tabId) {
  return normalizeProjectPortalTabId(tabId) === PROJECT_TABS.DOCUMENTS_REQUESTS;
}

export function isPhotosTab(tabId) {
  return normalizeProjectPortalTabId(tabId) === PROJECT_TABS.PHOTOS;
}

export function isOverviewTab(tabId) {
  return normalizeProjectPortalTabId(tabId) === PROJECT_TABS.OVERVIEW;
}

/**
 * Dashboard / list count for collaborators: use share count for package-scoped roles.
 */
export function resolveDashboardSharedDrawingCount({
  role = "",
  accessScope = "",
  allProjectFilesCount = 0,
  directShareCount = null,
  filesLoadedCount = 0,
} = {}) {
  const roleKey = safeStr(role).toLowerCase();
  const scope = safeStr(accessScope).toLowerCase();
  if (roleKey === "engineer" || scope === "shared_files") {
    if (directShareCount != null && Number.isFinite(Number(directShareCount))) {
      return Math.max(0, Number(directShareCount));
    }
    return Math.max(0, Number(filesLoadedCount) || 0);
  }
  return Math.max(0, Number(filesLoadedCount) || Number(allProjectFilesCount) || 0);
}
